<template>
    <div>
        <v-data-table
            :headers="useHeaders"
            :items="locations"
            disable-sort
            density="compact"
            color="primary"
            class="elevation-1 table-server mb-2"
            data-cy="table"
            :items-per-page="-1"
            hide-default-footer
        >
            <template v-slot:item.cityName="{item}">
                <span
                    class="text-subtitle-2 text-decoration-underline text-link"
                    :data-cy="`edit-location-${item.id}`"
                    @click="editLocation(item)"
                >
                    {{ item.cityName }}
                </span>
            </template>
            <template v-slot:item.countryName="{item}">
                <span
                    class="text-subtitle-2 text-decoration-underline text-link"
                    :data-cy="`edit-location-${item.id}`"
                    @click="editLocation(item)"
                >
                    {{ item.countryName }}
                </span>
            </template>
            <template v-if="!disabled" v-slot:item.action="{item}">
                <v-btn
                    variant="text"
                    color="primary"
                    class="mr-4 text-caption"
                    :data-cy="`delete-location-${item.id}`"
                    density="compact"
                    @click="deleteLocation(item.id)"
                >
                    {{ $t('delete') }}
                </v-btn>
            </template>
        </v-data-table>
        <v-btn v-if="!disabled" color="primary" data-cy="add" @click="addLocation()">
            {{ $t('add') }}
        </v-btn>
        <ModalWrapper v-model="isShowModal">
            <template v-slot:title>
                {{ $t('choose_country_city') }}
            </template>
            <template v-slot:content>
                <v-form ref="form" v-model="valid">
                    <v-row dense>
                        <v-col cols="12" sm="6">
                            <v-autocomplete
                                v-model="editablePoint.countryId"
                                :items="useCountries"
                                item-title="title"
                                item-value="value"
                                :label="`${$t('country')}*`"
                                closable-chips
                                clearable
                                density="compact"
                                color="primary"
                                data-cy="country-id"
                                :rules="[requiredRule('country')]"
                                @update:model-value="updateCountry()"
                            />
                        </v-col>
                        <v-col cols="12" sm="6">
                            <v-autocomplete
                                ref="cityRef"
                                v-model="editablePoint.cityId"
                                :items="cities"
                                item-title="title"
                                item-value="value"
                                :label="`${$t('city')}*`"
                                :loading="isCitiesLoading"
                                :disabled="isCitiesLoading || !cities.length"
                                closable-chips
                                clearable
                                density="compact"
                                color="primary"
                                data-cy="city-id"
                                :rules="[requiredRule('city')]"
                                @update:model-value="updateCity()"
                            />
                        </v-col>
                    </v-row>
                </v-form>
            </template>
            <template v-slot:btns>
                <v-btn data-cy="save" :disabled="isCitiesLoading || !valid" variant="flat" @click="savePoint()">
                    {{ $t('apply') }}
                </v-btn>
            </template>
        </ModalWrapper>
    </div>
</template>

<script setup lang="ts">
    import {useCountries} from '~/composables/form-helpers'
    import {requiredRule} from '~/composables/rules'
    import type {SelectOptionType} from '~/utils/types/types'
    import {nanoid} from 'nanoid'

    const isShowModal = ref<boolean>(false)
    const valid = ref<boolean>(false)
    const cities = ref<SelectOptionType[]>([])
    const isCitiesLoading = ref<boolean>(false)
    const editablePoint = ref({cityId: null, cityName: null, countryId: null, countryName: null, id: null})
    const cityRef = ref()

    const locations = defineModel<
        {
            cityId: number
            cityName: string
            countryId: number
            countryName: string
            id: number
        }[]
    >()
    const props = defineProps<{
        disabled?: boolean
    }>()
    const app = useNuxtApp()
    const {t} = useI18n()

    watch(useCountries, async () => {
        if (editablePoint.value.countryId) await loadCities(editablePoint.value.countryId)
    })

    const useHeaders = computed(() => {
        return [
            {title: t('city'), value: 'cityName'},
            {title: t('country'), value: 'countryName'},
            {title: '', value: 'action', minWidth: '120px', width: '10%'},
        ]
    })

    async function loadCities(countryId: number) {
        try {
            isCitiesLoading.value = true
            const rs = await app.$api.locations.get({
                limitCities: -1,
                orderBy: 'POPULARITY',
                countryId: countryId,
            })
            if (rs.cities) {
                return rs.cities.map(({id, name}) => {
                    return {title: name, value: id}
                })
            }
        } catch (e) {
            return []
        } finally {
            isCitiesLoading.value = false
        }
    }

    const addLocation = (): void => {
        editablePoint.value = {
            cityId: null,
            cityName: null,
            countryId: null,
            countryName: null,
            id: nanoid(),
        }
        isShowModal.value = true
    }

    const deleteLocation = (id): void => {
        locations.value = locations.value.filter(location => location.id !== id)
    }

    const updateCountry = async () => {
        const countryId = editablePoint.value.countryId
        if (editablePoint.value.cityId) cityRef.value.reset()
        editablePoint.value.cityName = null
        if (countryId) {
            cities.value = await loadCities(countryId)
            const selectedCountry = useCountries.value.find(country => country.value === editablePoint.value.countryId)
            editablePoint.value.countryName = selectedCountry.title || ''
        } else {
            cities.value = []
        }
    }

    const editLocation = async item => {
        if (props.disabled) return
        editablePoint.value = clone(item)
        cities.value = await loadCities(item.countryId)
        isShowModal.value = true
    }

    const updateCity = async () => {
        if (editablePoint.value.cityId) {
            const index = cities.value.findIndex(item => item.value === editablePoint.value.cityId)
            editablePoint.value.cityName = index !== -1 ? cities.value[index].title : ''
        }
    }

    const savePoint = () => {
        const index = locations.value.findIndex(item => item.id === editablePoint.value.id)
        if (index !== -1) {
            locations.value[index] = clone(editablePoint.value)
        } else {
            locations.value.push(clone(editablePoint.value))
        }

        isShowModal.value = false
    }
</script>
